import { makeIframeMessenger } from "./messenger";
import { MRAID_EVENT_ERROR } from "./mraid_constants";
import {
  MESSAGE_TOGGLE_MUTED,
  MESSAGE_CREATIVE_MADE_SOUND,
  MESSAGE_INTERACTION_CHANGE,
  MESSAGE_CLICKTHROUGH,
  MESSAGE_MRAID_EVENT,
  MESSAGE_MRAID_CALL,
  MESSAGE_INNER_ENV_INIT_COMPLETE,
  MESSAGE_OUTER_ENV_INIT_COMPLETE,
  MESSAGE_UPDATED_MRAID_PROPERTIES,
  MESSAGE_REQUEST_DOM_SNAPSHOT,
  MESSAGE_DOM_SNAPSHOT,
  MESSAGE_INNER_ENV_ERROR,
  MESSAGE_VUNGLE_CLICKTHROUGH,
  MESSAGE_TRESENSA_INTERACTION,
  MESSAGE_RESIZE,
} from "./messages";
import { PLATFORM_IOS } from "../../constants";

/*
 * Using document.write or srcdoc will cause document parsing to fail
 * midway if a script fails to load. This method uses DOMParser, which
 * builds a document with scripts that haven't loaded. Using DOMParser
 * and then reinserting all script tags allows us to get a consistent
 * DOM regardless of whether the scripts load or not (which can cause
 * flakes). See tests in content_frame.test.js that illustrate this
 * effect.
 */
// #if process.env.NODE_ENV === "test"
// const writeToIFrameThenLoadScripts = (iframeEl, html) => {
//   const parser = new DOMParser();
//   const parsedHTMLDocument = parser.parseFromString(html, "text/html");
// 
//   const iframeDocument = iframeEl.contentWindow.document;
//   const newDocFragment = iframeDocument.createDocumentFragment();
//   while (parsedHTMLDocument.childNodes.length > 0) {
//     newDocFragment.appendChild(parsedHTMLDocument.childNodes[0]);
//   }
// 
//   newDocFragment.querySelectorAll("script").forEach((brokenScript) => {
//     const fixedScript = iframeDocument.createElement("script");
//     if (brokenScript.src != null) {
//       fixedScript.src = brokenScript.src;
//     }
//     if (brokenScript.innerText != null) {
//       fixedScript.innerText = brokenScript.innerText;
//     }
//     fixedScript.type = brokenScript.type;
//     fixedScript.async = brokenScript.async;
//     brokenScript.parentNode.replaceChild(fixedScript, brokenScript);
//   });
// 
//   iframeDocument.replaceChild(newDocFragment);
// };
// #endif

class ContentFrame {
  // #if process.env.NODE_ENV === "test"
//   reset() {
//     this._messenger.reset();
//   }
  // #endif

  constructor({ envData, containerEl, processedHTML, name = "default" }) {
    this._iframeEl = document.createElement("iframe");

    // NOTE(dan): In the iOS WKWebView, nested iframes with the same src as
    // an ancestor iframe's src are not rendered to prevent infinite recursion.
    // You can see an example of this in
    // $REPOS/liftoff/exp/dan/nested-iframe-test. Below, about:blank (as opposed
    // to an arbitrary domain) prevents an additional request from being made in
    // Chrome. The query string was randomly chosen because about:blank on its
    // own is treated as equivalent to a src of "".
    if (envData.getDevicePlatform() === PLATFORM_IOS) {
      this._iframeEl.src = "about:blank?ym7eqam2cvea2hjm";
    }

    // NOTE(Austin): Maybe also worth it to investigate
    // the sandbox attribute here.
    Object.assign(this._iframeEl.style, {
      width: "100%",
      height: "100%",
      border: 0,
    });
    containerEl.appendChild(this._iframeEl);
    this._messenger = makeIframeMessenger(this._iframeEl, name);

    // We can construct promises here that don't involve callback arguments and
    // only are intended to be received once to avoid race conditions.
    this._initPromise = new Promise((resolve) => {
      this._messenger.once(MESSAGE_INNER_ENV_INIT_COMPLETE, () => {
        resolve(this);
      });
    });

    this._lastAllInteractionData = null;
    this.onInteractionChange((allInteractionData) => {
      this._lastAllInteractionData = allInteractionData;
    });

    this._isTresensaCreative = false;
    this.onTresensaInteraction(() => {
      this._isTresensaCreative = true;
    });

    // document.write fails in some cases (e.g. if the entire creative is
    // within a sandboxed iframe), while srcdoc fails in other cases
    // (which we don't totally understand), so we try both if necessary.
    // (This was found in A/B tests to work better than using either
    // method alone.)
    try {
      // #if process.env.NODE_ENV === "test"
//       writeToIFrameThenLoadScripts(this._iframeEl, processedHTML);
      // #endif
      // #if process.env.NODE_ENV !== "test"
      const iframeWindow = this._iframeEl.contentWindow;
      iframeWindow.document.open();
      iframeWindow.document.write(processedHTML);
      iframeWindow.document.close();
      // #endif
    } catch (_) {
      // NOTE(dan): This will overwrite the src attribute which is set above.
      this._iframeEl.srcdoc = processedHTML;
    }
  }

  _processInteractionPosition(interactionData) {
    const { left: iframeLeft, top: iframeTop } =
      this._iframeEl.getBoundingClientRect();
    const { x, startX, y, startY } = interactionData;
    const absoluteInteractionPosition = {
      x: x == null ? null : x + iframeLeft,
      y: y == null ? null : y + iframeTop,
      startX: startX == null ? null : startX + iframeLeft,
      startY: startY == null ? null : startY + iframeTop,
    };
    return { ...interactionData, ...absoluteInteractionPosition };
  }

  _processAllInteractionData(allInteractionData) {
    // allInteractionData can be null when the inner_env's
    // interaction monitor did not receive any events but
    // the frame navigated away anyways.
    return (
      allInteractionData && {
        ...allInteractionData,
        lastInteraction: this._processInteractionPosition(
          allInteractionData.lastInteraction
        ),
      }
    );
  }

  getInitCompletePromise() {
    return this._initPromise;
  }

  onClickthrough(callback) {
    this._messenger.on(
      MESSAGE_CLICKTHROUGH,
      (interceptedDestinationURL, customerQueryParams) => {
        callback(
          interceptedDestinationURL,
          this._lastAllInteractionData,
          customerQueryParams,
          this._isTresensaCreative
        );
      }
    );

    this._messenger.on(MESSAGE_VUNGLE_CLICKTHROUGH, () => {
      callback(null, this._lastAllInteractionData, {});
    });
  }

  onError(callback) {
    this._messenger.on(MESSAGE_INNER_ENV_ERROR, (normalizedError) => {
      callback(normalizedError);
    });
  }

  onMraidCall(callback) {
    this._messenger.on(MESSAGE_MRAID_CALL, callback);
  }

  updateMraidProperties(newMraidProperties) {
    this._messenger.post(MESSAGE_UPDATED_MRAID_PROPERTIES, newMraidProperties);
  }

  notifyOfMraidEvent(eventName, ...callbackArgs) {
    this._messenger.post(MESSAGE_MRAID_EVENT, eventName, callbackArgs);
  }

  notifyOfMraidError(errorMessage, methodName) {
    this.notifyOfMraidEvent(MRAID_EVENT_ERROR, errorMessage, methodName);
  }

  notifyOfInitComplete() {
    this._messenger.post(MESSAGE_OUTER_ENV_INIT_COMPLETE);
  }

  notifyOfResize() {
    this._messenger.post(MESSAGE_RESIZE);
  }

  onInteractionChange(callback) {
    this._messenger.on(MESSAGE_INTERACTION_CHANGE, (allInteractionData) => {
      callback(this._processAllInteractionData(allInteractionData));
    });
  }

  onTresensaInteraction(callback) {
    this._messenger.on(MESSAGE_TRESENSA_INTERACTION, callback);
  }

  onMadeSound(callback) {
    this._messenger.on(MESSAGE_CREATIVE_MADE_SOUND, callback);
  }

  setMuted(shouldMute) {
    this._messenger.post(MESSAGE_TOGGLE_MUTED, shouldMute);
  }

  getDOMSnapshot() {
    return new Promise((resolve) => {
      // Timeout after 1 second so we ensure the html return is posted.
      setTimeout(() => {
        resolve("<-- Timeout retrieving inner DOM snapshot -->");
      }, 1000);
      this._messenger.once(MESSAGE_DOM_SNAPSHOT, resolve);
      this._messenger.post(MESSAGE_REQUEST_DOM_SNAPSHOT);
    });
  }

  containsIframe() {
    return new Promise((resolve) => {
      this._initPromise.then(() => {
        const frame = this._iframeEl;
        resolve(frame.contentWindow.document.querySelector("iframe") != null);
      });
    });
  }
}

export default ContentFrame;



// WEBPACK FOOTER //
// ./src/js/madlib/csp-env/content_frame.js